import React, { useState, useEffect } from 'react';
import { UserPlus, AlertCircle, CheckCircle, Globe, Pencil, Trash2, Lock, Loader2, KeyRound, History, Copy, Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { adminApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const roleLabels = {
  'company_admin': 'Administrador',
  'compliance_officer': 'Compliance Officer',
  'triage_manager': 'Gestor de Triagem',
  'case_manager': 'Gestor de Denúncias',
  'dpo': 'DPO'
};

const roleColors = {
  'company_admin': 'bg-purple-100 text-purple-700',
  'compliance_officer': 'bg-blue-100 text-blue-700',
  'triage_manager': 'bg-amber-100 text-amber-700',
  'case_manager': 'bg-emerald-100 text-emerald-700',
  'dpo': 'bg-slate-100 text-slate-700'
};

const isVirtualCogniseg = (user) => (
  user?.is_cogniseg_global === true ||
  user?.virtual === true ||
  user?.email === 'gestortriagem@cogniseg.com'
);

const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'case_manager' });

  const [editUser, setEditUser] = useState(null); // {id, name, role, is_active}
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null); // user object
  const [deleting, setDeleting] = useState(false);

  const [resetTarget, setResetTarget] = useState(null); // user we're issuing reset for
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null); // {sent, reset_url, expires_minutes, email}

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [historyActionFilter, setHistoryActionFilter] = useState('all');
  const [historyUserFilter, setHistoryUserFilter] = useState('all');

  const fetchUsers = async () => {
    try {
      const response = await adminApi.getUsers();
      setUsers(response.data);
    } catch (err) {
      setError('Erro ao carregar utilizadores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleCreateUser = async () => {
    setCreating(true);
    setError('');
    try {
      await adminApi.createUser(newUser);
      setDialogOpen(false);
      setNewUser({ email: '', password: '', name: '', role: 'case_manager' });
      await fetchUsers();
      flashSuccess('Utilizador criado com sucesso');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar utilizador');
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (user) => {
    setError('');
    setEditUser({ id: user.id, name: user.name, role: user.role, is_active: user.is_active });
  };

  const handleUpdateUser = async () => {
    if (!editUser) return;
    setSaving(true);
    setError('');
    try {
      await adminApi.updateUser(editUser.id, {
        name: editUser.name,
        role: editUser.role,
        is_active: editUser.is_active,
      });
      setEditUser(null);
      await fetchUsers();
      flashSuccess('Utilizador atualizado');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar utilizador');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError('');
    try {
      await adminApi.deleteUser(deleteTarget.id);
      setDeleteTarget(null);
      await fetchUsers();
      flashSuccess('Utilizador removido');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao apagar utilizador');
    } finally {
      setDeleting(false);
    }
  };

  const handleSendResetLink = async () => {
    if (!resetTarget) return;
    setResetting(true);
    setError('');
    setResetResult(null);
    try {
      const { data } = await adminApi.sendUserResetLink(resetTarget.id);
      setResetResult({ ...data, email: resetTarget.email });
      if (data.sent) {
        flashSuccess(`Email de reset enviado para ${resetTarget.email}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar link de reset');
      setResetTarget(null);
    } finally {
      setResetting(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError('');
    setHistoryActionFilter('all');
    setHistoryUserFilter('all');
    try {
      const { data } = await adminApi.getAuditLogs({ resource_type: 'user', limit: 200 });
      setHistoryLogs(data.logs || []);
    } catch (err) {
      setHistoryError('Erro ao carregar histórico');
    } finally {
      setHistoryLoading(false);
    }
  };

  const getLogTargetEmail = (log) => (
    log.details?.target_email || log.details?.email || ''
  );

  const filteredHistoryLogs = historyLogs.filter((log) => {
    if (historyActionFilter !== 'all' && log.action !== historyActionFilter) return false;
    if (historyUserFilter !== 'all' && getLogTargetEmail(log) !== historyUserFilter) return false;
    return true;
  });

  const uniqueHistoryEmails = Array.from(new Set(
    historyLogs.map(getLogTargetEmail).filter(Boolean)
  )).sort();

  const uniqueHistoryActions = Array.from(new Set(
    historyLogs.map((l) => l.action)
  )).sort();

  const exportHistoryCsv = () => {
    const rows = filteredHistoryLogs.map((log) => {
      const changes = log.details?.changes
        ? Object.entries(log.details.changes)
            .map(([f, c]) => `${f}: ${c.from ?? ''} -> ${c.to ?? ''}`)
            .join('; ')
        : '';
      return {
        timestamp: log.created_at,
        action: log.action,
        actor_name: log.user_name || 'Sistema',
        actor_role: log.user_role || '',
        target_email: getLogTargetEmail(log),
        target_id: log.resource_id,
        changes,
        email_sent: log.details?.email_sent === undefined ? '' : String(log.details.email_sent),
      };
    });

    const headers = ['timestamp', 'action', 'actor_name', 'actor_role', 'target_email', 'target_id', 'changes', 'email_sent'];
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(','),
      ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
    ].join('\n');

    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historico-utilizadores-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    flashSuccess(`Exportadas ${rows.length} entradas para CSV`);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      flashSuccess('Link copiado para a área de transferência');
    } catch {
      // Fallback: select + execCommand is deprecated; just ignore
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Nunca';
    return new Date(dateStr).toLocaleDateString('pt-PT', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">A carregar...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Utilizadores
          </h1>
          <p className="text-slate-500 mt-1">{users.length} utilizadores registados</p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openHistory} data-testid="open-user-history-btn">
            <History className="w-4 h-4 mr-2" />
            Histórico
          </Button>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-user-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Novo Utilizador
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar Novo Utilizador</DialogTitle>
              <DialogDescription>Adicione um novo utilizador à plataforma</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input id="name" value={newUser.name} onChange={(e) => setNewUser(p => ({ ...p, name: e.target.value }))} placeholder="Nome completo" data-testid="new-user-name" />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={newUser.email} onChange={(e) => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="email@empresa.com" data-testid="new-user-email" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" value={newUser.password} onChange={(e) => setNewUser(p => ({ ...p, password: e.target.value }))} placeholder="Password segura" data-testid="new-user-password" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser(p => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button
                className="w-full"
                onClick={handleCreateUser}
                disabled={creating || !newUser.email || !newUser.password || !newUser.name}
                data-testid="create-user-btn"
              >
                {creating ? 'A criar...' : 'Criar Utilizador'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {error && (
        <Alert className="bg-red-50 border-red-200">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <AlertDescription className="text-red-700" data-testid="users-error">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700" data-testid="users-success">{success}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-4 font-medium text-slate-600">Nome</th>
                <th className="text-left p-4 font-medium text-slate-600">Email</th>
                <th className="text-left p-4 font-medium text-slate-600">Role</th>
                <th className="text-left p-4 font-medium text-slate-600">Estado</th>
                <th className="text-left p-4 font-medium text-slate-600">Último Login</th>
                <th className="text-right p-4 font-medium text-slate-600 w-52">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const isGlobal = isVirtualCogniseg(user);
                const isSelf = currentUser && user.id === currentUser.id;
                return (
                  <tr
                    key={user.id}
                    className={`border-b hover:bg-slate-50 ${isGlobal ? 'bg-indigo-50/40' : ''}`}
                    data-testid={isGlobal ? 'user-row-cogniseg-global' : `user-row-${user.id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{user.name}</p>
                        {isGlobal && (
                          <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200 gap-1" data-testid="global-badge">
                            <Globe className="w-3 h-3" />
                            Global
                          </Badge>
                        )}
                        {isSelf && !isGlobal && (
                          <Badge variant="outline" className="text-slate-500 border-slate-300">Você</Badge>
                        )}
                      </div>
                      {isGlobal && (
                        <p className="text-xs text-indigo-600/80 mt-1">
                          Gestor de Triagem CogniSeg (plataforma)
                        </p>
                      )}
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-slate-600">{user.email}</p>
                    </td>
                    <td className="p-4">
                      <Badge className={roleColors[user.role]}>
                        {roleLabels[user.role] || user.role}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className={user.is_active ? 'border-emerald-200 text-emerald-700' : 'border-red-200 text-red-700'}>
                        {user.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <span className="text-sm text-slate-500">
                        {isGlobal ? '—' : formatDate(user.last_login)}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        {isGlobal ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2.5 py-1"
                            title="Utilizador gerido pela plataforma CogniSeg"
                            data-testid="global-locked-indicator"
                          >
                            <Lock className="w-3 h-3" />
                            Gerido pela plataforma
                          </span>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(user)}
                              data-testid={`edit-user-${user.id}`}
                              title="Editar"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setResetResult(null); setResetTarget(user); }}
                              disabled={!user.is_active}
                              data-testid={`reset-user-${user.id}`}
                              title={user.is_active ? 'Enviar link de reset de password' : 'Ative a conta primeiro'}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <KeyRound className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteTarget(user)}
                              disabled={isSelf}
                              data-testid={`delete-user-${user.id}`}
                              title={isSelf ? 'Não pode apagar a sua própria conta' : 'Apagar'}
                              className={isSelf ? '' : 'text-red-600 hover:text-red-700 hover:bg-red-50'}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Editar Utilizador</DialogTitle>
            <DialogDescription>Atualize o nome, role ou estado do utilizador.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 mt-2">
              <div>
                <Label>Nome</Label>
                <Input
                  value={editUser.name}
                  onChange={(e) => setEditUser(p => ({ ...p, name: e.target.value }))}
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select
                  value={editUser.role}
                  onValueChange={(v) => setEditUser(p => ({ ...p, role: v }))}
                >
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <p className="text-sm font-medium text-slate-800">Conta ativa</p>
                  <p className="text-xs text-slate-500">Desativar bloqueia o login mas mantém o histórico.</p>
                </div>
                <Switch
                  checked={editUser.is_active}
                  onCheckedChange={(v) => setEditUser(p => ({ ...p, is_active: v }))}
                  data-testid="edit-user-active"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)} disabled={saving}>Cancelar</Button>
            <Button onClick={handleUpdateUser} disabled={saving || !editUser?.name?.trim()} data-testid="save-user-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle>Apagar utilizador</DialogTitle>
            <DialogDescription>
              {deleteTarget && (
                <>Tem a certeza que quer apagar <strong>{deleteTarget.name}</strong> ({deleteTarget.email})? Esta ação é irreversível.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancelar</Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              onClick={handleDeleteUser}
              disabled={deleting}
              data-testid="confirm-delete-user-btn"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Apagar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Send reset link dialog */}
      <Dialog
        open={!!resetTarget}
        onOpenChange={(o) => { if (!o) { setResetTarget(null); setResetResult(null); } }}
      >
        <DialogContent data-testid="reset-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-blue-600" />
              Enviar link de reset de password
            </DialogTitle>
            <DialogDescription>
              {resetTarget && !resetResult && (
                <>Vai ser gerado um link de reset único (válido 30 min) e enviado por email para <strong>{resetTarget.email}</strong>.</>
              )}
              {resetResult && (
                resetResult.sent
                  ? <>Email enviado para <strong>{resetResult.email}</strong>. O link expira em {resetResult.expires_minutes} minutos.</>
                  : <>O link foi gerado mas o email não pôde ser enviado (SendGrid não configurado). Copie e partilhe manualmente com o utilizador — expira em {resetResult.expires_minutes} minutos.</>
              )}
            </DialogDescription>
          </DialogHeader>

          {resetResult && (
            <div className="mt-2 space-y-2">
              <Label className="text-xs text-slate-500">Link de reset</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={resetResult.reset_url} className="font-mono text-xs" data-testid="reset-link-value" />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(resetResult.reset_url)}
                  data-testid="copy-reset-link-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            {!resetResult ? (
              <>
                <Button variant="outline" onClick={() => setResetTarget(null)} disabled={resetting}>Cancelar</Button>
                <Button onClick={handleSendResetLink} disabled={resetting} data-testid="confirm-send-reset-btn">
                  {resetting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Enviar link
                </Button>
              </>
            ) : (
              <Button onClick={() => { setResetTarget(null); setResetResult(null); }}>Fechar</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User history / audit dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="user-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-600" />
              Histórico de Utilizadores
            </DialogTitle>
            <DialogDescription>
              Todas as ações sobre utilizadores (criação, alteração de role/estado, remoção, reset de password).
            </DialogDescription>
          </DialogHeader>

          {/* Filters + export */}
          {!historyLoading && !historyError && historyLogs.length > 0 && (
            <div className="flex flex-wrap items-end gap-3 border-b pb-3">
              <div className="flex-1 min-w-[180px]">
                <Label className="text-xs text-slate-500">Ação</Label>
                <Select value={historyActionFilter} onValueChange={setHistoryActionFilter}>
                  <SelectTrigger data-testid="history-action-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as ações</SelectItem>
                    {uniqueHistoryActions.map((a) => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[220px]">
                <Label className="text-xs text-slate-500">Utilizador afectado</Label>
                <Select value={historyUserFilter} onValueChange={setHistoryUserFilter}>
                  <SelectTrigger data-testid="history-user-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os utilizadores</SelectItem>
                    {uniqueHistoryEmails.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={exportHistoryCsv}
                disabled={filteredHistoryLogs.length === 0}
                data-testid="export-history-csv-btn"
                title="Exportar registos filtrados em CSV"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto pr-1 -mr-1">
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : historyError ? (
              <div className="text-center py-12 text-red-500">{historyError}</div>
            ) : historyLogs.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Sem registos ainda.</p>
              </div>
            ) : filteredHistoryLogs.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>Nenhum registo corresponde aos filtros.</p>
              </div>
            ) : (
              <ul className="divide-y" data-testid="user-history-list">
                {filteredHistoryLogs.map((log) => (
                  <li key={log.id} className="py-3 flex items-start gap-3">
                    <div className="mt-0.5">
                      {log.action === 'USER_CREATED' && <UserPlus className="w-4 h-4 text-emerald-600" />}
                      {log.action === 'USER_UPDATED' && <Pencil className="w-4 h-4 text-blue-600" />}
                      {log.action === 'USER_DELETED' && <Trash2 className="w-4 h-4 text-red-600" />}
                      {log.action === 'USER_PASSWORD_RESET_SENT' && <KeyRound className="w-4 h-4 text-amber-600" />}
                      {!['USER_CREATED','USER_UPDATED','USER_DELETED','USER_PASSWORD_RESET_SENT'].includes(log.action) && (
                        <History className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs font-mono">{log.action}</Badge>
                        <span className="text-sm text-slate-700">
                          {log.details?.target_email || log.details?.email || log.resource_id}
                        </span>
                      </div>
                      {log.details?.changes && (
                        <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                          {Object.entries(log.details.changes).map(([field, chg]) => (
                            <div key={field}>
                              <span className="font-medium">{field}</span>: <span className="line-through">{String(chg.from ?? '—')}</span> → <span className="text-slate-800">{String(chg.to ?? '—')}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {log.action === 'USER_CREATED' && log.details?.role && (
                        <div className="text-xs text-slate-500 mt-1">Role: {log.details.role}</div>
                      )}
                      {log.action === 'USER_PASSWORD_RESET_SENT' && (
                        <div className="text-xs text-slate-500 mt-1">
                          Email {log.details?.email_sent ? 'enviado' : 'não enviado (fallback manual)'}
                        </div>
                      )}
                      <div className="text-xs text-slate-400 mt-1">
                        {log.user_name || 'Sistema'} · {new Date(log.created_at).toLocaleString('pt-PT')}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t pt-3 mt-2 text-xs text-slate-500 flex items-center justify-between">
            <span>{filteredHistoryLogs.length} de {historyLogs.length} registo(s)</span>
            {(historyActionFilter !== 'all' || historyUserFilter !== 'all') && (
              <button
                type="button"
                onClick={() => { setHistoryActionFilter('all'); setHistoryUserFilter('all'); }}
                className="text-slate-600 hover:text-slate-800 underline underline-offset-2"
                data-testid="clear-history-filters-btn"
              >
                Limpar filtros
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
